/**
 * Client for app.query_shared_table: full-result server-side filtering and
 * sorting with signed, expiring, query-bound keyset cursors.
 *
 * Contract reminders (TABLE-QUERY-CONTRACT.md):
 *  - Only allowlisted datasets; every page rechecks current authority.
 *  - Pages carry no count; never present loaded rows as a total. Exact totals come
 *    from app.count_shared_table (same filters, capped), via countQuery.
 *  - Live edits can move rows between pages, so rows are keyed by stable ID
 *    and a repeated ID updates the existing row instead of duplicating it.
 *  - Text order is lowercase C collation, not locale-natural order.
 */
import type { Transport } from './transport.ts';
import { classifyFailure, type Failure } from './errors.ts';

export type FilterOp = 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'in' | 'contains' | 'prefix' | 'is_null' | 'not_null';
export interface Filter { field: string; op: FilterOp; value?: string; values?: string[] }
export interface Sort { field: string; direction: 'asc' | 'desc' }
export interface QuerySpec {
  dataset: string;
  scope?: string | null;
  subScope?: string | null;
  historical?: boolean;
  filters: Filter[];
  sorts: Sort[];
  pageSize?: number;
}

export const QUERY_LIMITS = { filters: 12, inValues: 30, sorts: 3, pageSize: 100, textValue: 500 } as const;

/** Validates locally so obviously invalid queries never reach the server. */
export function validateSpec(spec: QuerySpec): string | null {
  if (spec.filters.length > QUERY_LIMITS.filters) return 'too_many_filters';
  if (spec.sorts.length > QUERY_LIMITS.sorts) return 'too_many_sorts';
  const size = spec.pageSize ?? 50;
  if (!Number.isInteger(size) || size < 1 || size > QUERY_LIMITS.pageSize) return 'invalid_page_size';
  const fields = new Set<string>();
  for (const sort of spec.sorts) {
    if (fields.has(sort.field)) return 'duplicate_sort';
    fields.add(sort.field);
  }
  for (const filter of spec.filters) {
    if (filter.op === 'in' && (!filter.values || filter.values.length < 1 || filter.values.length > QUERY_LIMITS.inValues)) return 'invalid_in';
    if ((filter.op === 'contains' || filter.op === 'prefix') && (!filter.value || filter.value.length > QUERY_LIMITS.textValue)) return 'invalid_text';
  }
  return null;
}

/** Stable identity for a query; selection-of-all-matching binds to it. */
export function fingerprint(workspaceId: string, spec: QuerySpec): string {
  return JSON.stringify([workspaceId, spec.dataset, spec.scope ?? null, spec.subScope ?? null, spec.historical ?? false,
    spec.filters.map(f => [f.field, f.op, f.value ?? null, f.values ?? null]), spec.sorts.map(s => [s.field, s.direction])]);
}

interface PageResponse<Raw> { rows: Raw[]; next_cursor: string | null }

export interface QueryState<Row> {
  status: 'idle' | 'loading' | 'ready' | 'error';
  /** Rows in server order, unique by key. */
  rows: readonly Row[];
  hasMore: boolean;
  /** True while a refresh is replacing previously shown rows. */
  refreshing: boolean;
  /** Set when the most recent request failed; earlier rows stay visible. */
  failure: Failure | null;
  /** Wall-clock time of the last successful page (freshness of the DB read, not of providers). */
  loadedAt: number | null;
  /** Bounded memory: further paging requires narrowing the query. */
  reachedClientLimit: boolean;
}

export interface PagedQueryOptions<Raw, Row> {
  transport: Transport;
  workspaceId: string;
  spec: QuerySpec;
  key: (raw: Raw) => string;
  /** Converts raw rows once on arrival (formatting/parsing stays out of render). */
  map: (raw: Raw) => Row;
  maxRows?: number;
}

/** Owns one query's pages. Superseded responses are discarded by generation. */
export class PagedQuery<Raw, Row> {
  readonly fingerprint: string;
  #opts: PagedQueryOptions<Raw, Row>;
  #state: QueryState<Row> = { status: 'idle', rows: [], hasMore: false, refreshing: false, failure: null, loadedAt: null, reachedClientLimit: false };
  #index = new Map<string, number>();
  #cursor: string | null = null;
  #generation = 0;
  #inflight: Promise<void> | null = null;
  #abort: AbortController | null = null;
  #listeners = new Set<() => void>();

  constructor(opts: PagedQueryOptions<Raw, Row>) {
    const invalid = validateSpec(opts.spec);
    if (invalid) throw Error(invalid);
    this.#opts = opts;
    this.fingerprint = fingerprint(opts.workspaceId, opts.spec);
  }

  get state(): QueryState<Row> { return this.#state; }
  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }
  #set(patch: Partial<QueryState<Row>>) {
    this.#state = { ...this.#state, ...patch };
    for (const listener of this.#listeners) listener();
  }

  /** Loads the first page (or reloads from the start, keeping rows visible until replaced). */
  refresh(): Promise<void> {
    this.#abort?.abort();
    this.#generation++;
    this.#cursor = null;
    this.#inflight = null;
    this.#set({ status: 'loading', refreshing: this.#state.rows.length > 0, failure: null });
    return this.#load(true);
  }

  /** Loads the next page. Concurrent calls share one request. */
  loadMore(): Promise<void> {
    if (this.#inflight) return this.#inflight;
    if (!this.#state.hasMore || this.#state.reachedClientLimit) return Promise.resolve();
    this.#set({ status: 'loading', failure: null });
    return this.#load(false);
  }

  dispose() {
    this.#abort?.abort();
    this.#generation++;
    this.#listeners.clear();
  }

  #load(first: boolean, attempt = 0, restarted = false): Promise<void> {
    const generation = this.#generation;
    const abort = new AbortController();
    this.#abort = abort;
    const { transport, workspaceId, spec } = this.#opts;
    const usedCursor = first ? null : this.#cursor;
    const run = (async () => {
      try {
        const page = await transport.rpc<PageResponse<Raw>>('query_shared_table', {
          w: workspaceId, dataset: spec.dataset, scope: spec.scope ?? null, sub_scope: spec.subScope ?? null,
          historical: spec.historical ?? false, filters: spec.filters, sorts: spec.sorts,
          page_size: spec.pageSize ?? 50, cursor: usedCursor,
        }, abort.signal);
        if (generation !== this.#generation) return;
        this.#apply(page, first);
      } catch (error) {
        if (generation !== this.#generation) return;
        const failure = classifyFailure({ error, operation: 'read', attempt, pagedWithCursor: usedCursor !== null });
        if (failure.kind === 'cancelled') return;
        if (failure.kind === 'query_restart' && !restarted) {
          // Expired or rebound cursor: restart from the first page once.
          this.#inflight = null;
          this.#set({ refreshing: this.#state.rows.length > 0 });
          return this.#load(true, 0, true);
        }
        if (failure.retry) {
          await new Promise(resolve => setTimeout(resolve, failure.delayMs));
          if (generation !== this.#generation) return;
          this.#inflight = null;
          return this.#load(first, attempt + 1, restarted);
        }
        this.#set({
          status: 'error', failure, refreshing: false,
          // Revoked access: drop protected rows instead of showing them as current.
          ...(failure.purgeProtectedCache ? { rows: [], hasMore: false } : {}),
        });
        if (failure.purgeProtectedCache) this.#index.clear();
      } finally {
        if (generation === this.#generation && this.#abort === abort) this.#inflight = null;
      }
    })();
    this.#inflight = run;
    return run;
  }

  #apply(page: PageResponse<Raw>, first: boolean) {
    const { key, map, maxRows = 5000 } = this.#opts;
    let rows: Row[];
    if (first) {
      rows = [];
      this.#index.clear();
    } else {
      rows = this.#state.rows.slice();
    }
    for (const raw of page.rows) {
      const id = key(raw);
      const mapped = map(raw);
      const existing = this.#index.get(id);
      if (existing === undefined) {
        this.#index.set(id, rows.length);
        rows.push(mapped);
      } else {
        rows[existing] = mapped; // live edit moved the row; keep one identity
      }
    }
    this.#cursor = page.next_cursor;
    this.#set({
      status: 'ready', rows, refreshing: false, failure: null, loadedAt: Date.now(),
      hasMore: page.next_cursor !== null, reachedClientLimit: rows.length >= maxRows,
    });
  }

  /** Replaces one row after a confirmed readback without reloading the list. */
  replaceRow(raw: Raw) {
    const id = this.#opts.key(raw);
    const position = this.#index.get(id);
    if (position === undefined) return;
    const rows = this.#state.rows.slice();
    rows[position] = this.#opts.map(raw);
    this.#set({ rows });
  }
}

/** Exact match count for a spec, or a lower bound when capped. */
export interface CountResult { count: number; capped: boolean }
export async function countQuery(transport: Transport, workspaceId: string, spec: Pick<QuerySpec, 'dataset' | 'scope' | 'subScope' | 'historical' | 'filters'>, signal?: AbortSignal, cap?: number): Promise<CountResult> {
  const result = await transport.rpc<CountResult>('count_shared_table', {
    w: workspaceId, dataset: spec.dataset, scope: spec.scope ?? null, filters: spec.filters,
    sub_scope: spec.subScope ?? null, historical: spec.historical ?? false, ...(cap ? { cap } : {}),
  }, signal);
  return { count: Number(result.count), capped: Boolean(result.capped) };
}

/** Reads one row by key through the same authorized projection (used for readback). */
export async function readOne<Raw>(transport: Transport, workspaceId: string, dataset: string, keyField: string, id: string, scope?: string | null): Promise<Raw | null> {
  const page = await transport.rpc<PageResponse<Raw>>('query_shared_table', {
    w: workspaceId, dataset, scope: scope ?? null, filters: [{ field: keyField, op: 'eq', value: id }], sorts: [], page_size: 1,
  });
  return page.rows[0] ?? null;
}
